import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import useSWR from 'swr';
import * as Yup from 'yup';
import { testImage } from '../../lib/utils';
import { JSONClient, fetcher } from '../../api/bootstrap';
import { MerchantItemForm } from './MerchantItemForm';
import { WHITE_LABEL_ACCENT_COLOR } from '../../lib/white-label';
import styled from 'styled-components';

const AddItemSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Item name must be at least 2 characters')
    .required('Required'),
  slug: Yup.string()
    .min(1, 'URL Slug must be at least 1 characters')
    .test(
      'no-spaces',
      'slug cannot have spaces',
      (value, context) => !value?.includes(' '),
    )
    .required('Required'),
  description: Yup.string()
    .min(2, 'Item description must be at least 2 characters')
    .required('Required'),
  metaDescription: Yup.string()
    .min(1, 'Item meta description must be at least 1 characters')
    .max(200, 'Item meta description must not exceed 200 characters'),
  imageUrl: Yup.string()
    .test('valid-image-url', 'Must use valid image URL', (value) =>
      testImage(value, 1000).then((status) => status === 'success'),
    )
    .required('Required'),
  logoUrl: Yup.string()
    .test('valid-image-url', 'Must use valid image URL', (value) =>
      testImage(value, 1000).then((status) => status === 'success'),
    )
    .required('Required'),
  bannerUrl: Yup.string()
    .test('valid-image-url', 'Must use valid image URL', (value) =>
      testImage(value, 1000).then((status) => status === 'success'),
    )
    .required('Required'),
  categoryId: Yup.string().required('Required'),
  categoryItemOrder: Yup.number().nullable(true),
});

const UpdateLink = styled.a`
  color: ${WHITE_LABEL_ACCENT_COLOR};

  &:hover {
    opacity: 0.75;
  }
`;

const UpdateItem = ({ onClose, values }) => {
  const [show, setShow] = useState(false);
  const handleClose = () => {
    setShow(false);
    if (onClose) {
      onClose();
    }
  };
  const handleShow = () => {
    setShow(true);
  };

  const { data: categories } = useSWR('/market/category');

  const formik = useFormik({
    initialValues: {
      name: values.name,
      slug: values.slug,
      description: values.description,
      metaDescription: values.metaDescription,
      imageUrl: values.imageUrl,
      categoryId: values.categoryId,
      bannerUrl: values.bannerUrl,
      logoUrl: values.logoUrl,
      categoryItemOrder: values.categoryItemOrder || null,
    },
    validationSchema: AddItemSchema,
    onSubmit: async (
      formValues,
      { setSubmitting, setStatus, setFieldError },
    ) => {
      setSubmitting(true);
      const updateValues: any = {};

      Object.keys(formValues).forEach((key) => {
        if (formik.initialValues[key] !== formValues[key]) {
          updateValues[key] = formValues[key];
        }
      });

      try {
        if (updateValues) {
          if (updateValues.slug) {
            const res = await fetcher(`/market/item/slug/${updateValues.slug}`);
            if (res?.exists === true) {
              return setFieldError('slug', 'Slug is not unique');
            }
          }

          const { data } = await JSONClient.put(
            `${process.env.SHOP_BACKEND_URI}/market/item/${values.id}`,
            {
              ...updateValues,
            },
          );
        }
        handleClose();
      } catch (error) {
        console.error(error);
        setStatus(error?.response?.data);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!show) {
      // if hidden for any reason we should make sure form is reset
      formik.resetForm({
        values: {
          name: values.name,
          slug: values.slug,
          description: values.description,
          metaDescription: values.metaDescription,
          imageUrl: values.imageUrl,
          bannerUrl: values.bannerUrl,
          logoUrl: values.logoUrl,
          categoryId: values.categoryId,
          categoryItemOrder: values.categoryItemOrder,
        },
      });
      formik.setStatus('valid');
    }
  }, [show, values]);

  return (
    <div>
      <UpdateLink href="#" onClick={handleShow}>
        Edit
      </UpdateLink>
      {show ? (
        <MerchantItemForm
          handleClose={handleClose}
          categories={categories}
          formik={formik}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default UpdateItem;
